import React, { useCallback } from 'react';
import ListView from 'screens/Applications&WorkOrders/Components/ListView';
import { FILTER_LABEL } from 'screens/Applications&WorkOrders/Components/Constants';
import SortDropdownMenu from 'screens/Applications&WorkOrders/Components/SortDropdownMenu';
import { ApplicationSummary } from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import {
  usePagination,
  useSearch,
  useSort,
  useSummaryParams
} from 'screens/Applications&WorkOrders/Context/SummaryParamsContext';
import useListViewSelection from 'screens/Applications&WorkOrders/Hooks/useListViewSelection';
import useListViewColumns from 'screens/Applications&WorkOrders/Hooks/useListViewColumns';
import FilterDrawer from 'screens/Applications&WorkOrders/Components/FilterDrawer';
import OperationDetailPanel from 'screens/Applications&WorkOrders/Components/OperationDetailPanel';
import { OperationType } from 'screens/Applications&WorkOrders/RouteAdmin';
import useOperationEvents from 'screens/Applications&WorkOrders/Hooks/useOperationEvents';
import useTagsData from 'hooks/useTagsData';

export interface OperationListViewConfig {
  icon: React.ReactNode;
  title: string;
  searchPlaceholder: string;
  emptyMessage: string;
  operationType: OperationType;
  onCreateClick: () => void;
}

interface OperationListViewProps {
  items: ApplicationSummary[];
  totalElements: number;
  currentPage: number;
  pageSize: number;
  isLoading?: boolean;
  onItemDeleted?: (itemId: string) => void;
  hasFilters?: boolean;
  config: OperationListViewConfig;
}

const OperationListView: React.FC<OperationListViewProps> = ({
  items,
  totalElements,
  currentPage,
  pageSize,
  isLoading = false,
  hasFilters = false,
  onItemDeleted,
  config
}) => {
  const { sendViewEvent } = useOperationEvents(config.operationType);
  const { searchTerm, handleSearchChange } = useSearch();
  const { config: paginationConfig } = usePagination({
    currentPage,
    totalElements,
    pageSize
  });

  const {
    filters,
    isDrawerVisible: isFilterDrawerVisible,
    setIsDrawerVisible: setIsFilterDrawerVisible,
    handleFiltersChange
  } = useSummaryParams();

  useSort();

  const { tags: rawTags } = useTagsData();

  const { selectedId, handleSelect, handleItemDeleted } = useListViewSelection({
    items,
    isLoading
  });

  const handleFilterClick = () => {
    setIsFilterDrawerVisible(true);
  };

  const handleOperationDeleted = useCallback(
    (deletedId: string) => {
      handleItemDeleted(deletedId, onItemDeleted);
    },
    [handleItemDeleted, onItemDeleted]
  );

  const { rowSelection, columns } = useListViewColumns({
    selectedId,
    onSelectId: handleSelect,
    allTags: rawTags,
    searchTerm
  });

  return (
    <ListView
      icon={config.icon}
      sortDropdown={<SortDropdownMenu />}
      title={config.title}
      searchPlaceholder={config.searchPlaceholder}
      pagination={paginationConfig}
      searchText={searchTerm}
      onSearchChange={handleSearchChange}
      createButtonLabel="Create New"
      onCreateClick={config.onCreateClick}
      filterButtonLabel={FILTER_LABEL}
      onFilterClick={handleFilterClick}
      hasActiveFilters={hasFilters}
      dataSource={items}
      columns={columns}
      rowKey="id"
      rowSelection={rowSelection}
      onRowClick={record => {
        sendViewEvent();
        handleSelect(record.id);
      }}
      isLoading={isLoading}
      emptyMessage={config.emptyMessage}
      detailPanel={
        <OperationDetailPanel
          operationId={selectedId}
          operationType={config.operationType}
          isLoading={isLoading}
          onOperationDeleted={handleOperationDeleted}
          hasOperations={items.length > 0}
        />
      }
      filterDrawer={
        <FilterDrawer
          initialValues={filters}
          visible={isFilterDrawerVisible}
          onFiltersChange={handleFiltersChange}
          onClose={() => setIsFilterDrawerVisible(false)}
          operationType={config.operationType}
        />
      }
    />
  );
};

export default OperationListView;
